import { z } from 'zod';
import { BusinessCodeSchema, EnteredNameSchema, RecordIdSchema } from './identifiers';
import { QuantitySchema } from './invoicing';
import { DecimalStringSchema, MoneySchema } from './money';
import { BusinessDateSchema, InstantSchema } from './time';

/**
 * Warehouses and materials — BMP-2 package 4b (`WH-ITEM-001`, `WH-TRANS-001`).
 *
 * Items are kept in one **base unit**; other units convert to it by an exact factor. Every change of a
 * store's quantity is one row of an append-only **stock ledger**, written in the same transaction as
 * the document that caused it (an opening, a receipt from a purchase order, an issue to a project or
 * a person, a return, a transfer, an approved adjustment or count difference). The balance of a store
 * is kept beside the ledger and changed only by a conditional update, so two issues of the same stock
 * at once cannot both take the last unit.
 *
 * Negative stock (`BD-48`, setting `stores.negativeStock`) is refused unless the client allows it.
 * **Valuation is not decided** (`BD-67`: FIFO, weighted average or standard cost): quantities are
 * exact and complete; no stock value is computed and no stock event is posted to the ledger until the
 * method is chosen. Receipts carry the order price for information only.
 */

/**
 * A reference-list code (as `ReferenceCodeSchema` in settings, which imports this file — so it is
 * repeated here rather than imported).
 */
const ReferenceCodeSchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9][A-Za-z0-9_-]{0,39}$/, { message: 'REFERENCE_CODE_EXPECTED' });

/* ------------------------------------------------------------------- items */

export const ITEM_STATES = ['active', 'inactive'] as const;
export const ItemStateSchema = z.enum(ITEM_STATES);

const UnitSchema = z.string().trim().min(1).max(20);
/** How many base units one of this unit holds — exact, positive, at most six decimals. */
const FactorSchema = DecimalStringSchema.refine(
  (value) => !value.startsWith('-') && !/^0(\.0+)?$/.test(value) && !/\.\d{7,}$/.test(value),
  { message: 'FACTOR_EXPECTED' },
);

export const UnitConversionSchema = z.strictObject({ unit: UnitSchema, factor: FactorSchema });
export type UnitConversion = z.infer<typeof UnitConversionSchema>;

export const StockItemSchema = z.strictObject({
  itemId: RecordIdSchema,
  code: BusinessCodeSchema,
  name: EnteredNameSchema,
  /** The name in the other language, where the item has one. */
  alternateName: z.string().optional(),
  /** Free-text category kept from before the configurable categories. */
  category: z.string().optional(),
  /** A code from the dashboard-configured `stockItemCategories` list. */
  categoryCode: z.string().optional(),
  baseUnit: UnitSchema,
  conversions: z.array(UnitConversionSchema),
  /** Keep a batch (lot) number on every movement of this item. */
  batchTracked: z.boolean(),
  /** Below this quantity in a store the item appears on the reorder list (base units). */
  reorderLevel: DecimalStringSchema.optional(),
  state: ItemStateSchema,
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type StockItem = z.infer<typeof StockItemSchema>;

export const CreateStockItemSchema = z
  .strictObject({
    code: BusinessCodeSchema,
    name: EnteredNameSchema,
    alternateName: z.string().trim().min(1).max(160).optional(),
    category: z.string().trim().min(1).max(60).optional(),
    categoryCode: ReferenceCodeSchema.optional(),
    baseUnit: UnitSchema,
    conversions: z.array(UnitConversionSchema).max(10).default([]),
    batchTracked: z.boolean().default(false),
    reorderLevel: QuantitySchema.optional(),
  })
  .refine(
    (value) =>
      new Set(value.conversions.map((row) => row.unit)).size === value.conversions.length &&
      !value.conversions.some((row) => row.unit === value.baseUnit),
    { message: 'UNIT_DUPLICATE', path: ['conversions'] },
  );
export type CreateStockItem = z.infer<typeof CreateStockItemSchema>;

export const UpdateStockItemSchema = z.strictObject({
  name: EnteredNameSchema.optional(),
  alternateName: z.string().trim().min(1).max(160).nullable().optional(),
  category: z.string().trim().min(1).max(60).nullable().optional(),
  categoryCode: ReferenceCodeSchema.nullable().optional(),
  /** New units may be added; an existing unit's factor never changes once used. */
  conversions: z.array(UnitConversionSchema).max(10).optional(),
  reorderLevel: QuantitySchema.nullable().optional(),
  state: ItemStateSchema.optional(),
  reason: z.string().trim().min(3).max(500),
  expectedVersion: z.number().int().positive(),
});
export type UpdateStockItem = z.infer<typeof UpdateStockItemSchema>;

export const ItemQuerySchema = z.strictObject({
  search: z.string().trim().max(80).optional(),
  state: ItemStateSchema.optional(),
  categoryCode: ReferenceCodeSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.string().min(1).max(200).optional(),
});
export type ItemQuery = z.infer<typeof ItemQuerySchema>;

/* -------------------------------------------------------------- warehouses */

export const WAREHOUSE_KINDS = ['central', 'project', 'site'] as const;
export const WarehouseKindSchema = z.enum(WAREHOUSE_KINDS);

export const WarehouseSchema = z.strictObject({
  warehouseId: RecordIdSchema,
  code: BusinessCodeSchema,
  name: EnteredNameSchema,
  kind: WarehouseKindSchema,
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema,
  projectId: RecordIdSchema.optional(),
  /**
   * The store this one sits under (a zone of a central store, a site store under a project store).
   * Stock is always held by the leaf it moved into; a parent's figures add up its children.
   */
  parentWarehouseId: RecordIdSchema.optional(),
  /** Where it is: an address, yard or building. */
  location: z.string().optional(),
  /** The storekeeper — custodian of what the store holds; acknowledges transfers into it. */
  keeperAccountId: z.string(),
  state: ItemStateSchema,
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type Warehouse = z.infer<typeof WarehouseSchema>;

export const CreateWarehouseSchema = z.strictObject({
  code: BusinessCodeSchema,
  name: EnteredNameSchema,
  kind: WarehouseKindSchema,
  branchId: RecordIdSchema,
  projectId: RecordIdSchema.optional(),
  parentWarehouseId: RecordIdSchema.optional(),
  location: z.string().trim().min(1).max(300).optional(),
  keeperAccountId: z.string().min(1).max(200),
});
export type CreateWarehouse = z.infer<typeof CreateWarehouseSchema>;

export const UpdateWarehouseSchema = z.strictObject({
  name: EnteredNameSchema.optional(),
  /** null takes the store out of its parent. */
  parentWarehouseId: RecordIdSchema.nullable().optional(),
  location: z.string().trim().min(1).max(300).nullable().optional(),
  keeperAccountId: z.string().min(1).max(200).optional(),
  state: ItemStateSchema.optional(),
  reason: z.string().trim().min(3).max(500),
  expectedVersion: z.number().int().positive(),
});

/* -------------------------------------------------------- stock documents */

export const STOCK_DOCUMENT_KINDS = [
  'opening',
  'receipt',
  'issue',
  'return',
  'transfer',
  'adjustment',
  /** Accepted purchase goods sent back to the supplier — recorded only by procurement (PROC-RECEIPT-001). */
  'supplierReturn',
] as const;
export const StockDocumentKindSchema = z.enum(STOCK_DOCUMENT_KINDS);
export type StockDocumentKind = z.infer<typeof StockDocumentKindSchema>;

/**
 * `posted` — the stock moved. A transfer is `inTransit` once it left the source store and `posted`
 * when the receiving keeper acknowledged it. An adjustment may wait for approval (`pendingApproval`)
 * where a policy governs `stores.adjustment`. `cancelled` and `rejected` moved nothing (or moved it
 * back — a cancelled transfer returns to its source).
 */
export const STOCK_DOCUMENT_STATES = [
  'posted',
  'inTransit',
  'pendingApproval',
  'rejected',
  'cancelled',
  'reversed',
] as const;
export const StockDocumentStateSchema = z.enum(STOCK_DOCUMENT_STATES);
export type StockDocumentState = z.infer<typeof StockDocumentStateSchema>;

/**
 * `custody`: company material handed to someone who must give it back (tools, formwork) — recorded
 * through a custody record, never directly.
 */
export const ISSUE_TARGETS = ['project', 'person', 'other', 'custody'] as const;

/** A project phase or work package, as the construction module and project costs label it. */
const PhaseSchema = z.string().trim().min(1).max(80);
/** The cost code the client books the material under. */
const CostCodeSchema = z.string().trim().min(1).max(40);

const IssuedToSchema = z.strictObject({
  kind: z.enum(ISSUE_TARGETS),
  projectId: RecordIdSchema.optional(),
  /** Project consumption: the phase and cost code it is booked under (WH-PROJECT-001). */
  phase: PhaseSchema.optional(),
  costCode: CostCodeSchema.optional(),
  accountId: z.string().optional(),
  description: z.string().optional(),
  custodyId: RecordIdSchema.optional(),
});

export const StockLineInputSchema = z.strictObject({
  itemId: RecordIdSchema,
  /** As entered; converted to the item's base unit with its declared factor. */
  quantity: QuantitySchema,
  unit: UnitSchema,
  batch: z.string().trim().min(1).max(60).optional(),
  /** Adjustments only: the direction of the change. */
  direction: z.enum(['increase', 'decrease']).optional(),
  note: z.string().trim().max(300).optional(),
  /**
   * Cost per entered unit, in the company's currency — required on openings and manual receipts once
   * stock valuation is adopted (BD-67); purchase receipts take it from the order.
   */
  unitCost: DecimalStringSchema.optional(),
});
export type StockLineInput = z.infer<typeof StockLineInputSchema>;

export const StockLineSchema = StockLineInputSchema.extend({
  lineNo: z.number().int().positive(),
  /** Signed quantity in the item's base unit, as it moved the source store (negative = out). */
  baseQuantity: DecimalStringSchema,
  baseUnit: UnitSchema,
});
export type StockLine = z.infer<typeof StockLineSchema>;

export const StockDocumentSchema = z.strictObject({
  documentId: RecordIdSchema,
  number: z.string(),
  kind: StockDocumentKindSchema,
  state: StockDocumentStateSchema,
  warehouseId: RecordIdSchema,
  /** Transfers: the receiving store. */
  toWarehouseId: RecordIdSchema.optional(),
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema,
  projectId: RecordIdSchema.optional(),
  movedOn: BusinessDateSchema,
  lines: z.array(StockLineSchema),
  /** Issues: who or what received the material. Returns from use: where it came back from. */
  issuedTo: IssuedToSchema.optional(),
  /** What caused it, when another module did (a goods receipt, a count). */
  source: z.strictObject({ type: z.string(), id: z.string() }).optional(),
  reason: z.string().optional(),
  recordedByAccountId: z.string(),
  acknowledgedByAccountId: z.string().optional(),
  acknowledgedAt: InstantSchema.optional(),
  /**
   * Transfers: per line, what the receiving keeper accepted, what went back to the source store, and
   * what is still in transit — in the line's unit. Present on every transfer.
   */
  transferLines: z
    .array(
      z.strictObject({
        lineNo: z.number().int().positive(),
        quantity: DecimalStringSchema,
        received: DecimalStringSchema,
        returned: DecimalStringSchema,
        inTransit: DecimalStringSchema,
      }),
    )
    .optional(),
  transferReceipts: z
    .array(
      z.strictObject({
        at: InstantSchema,
        byAccountId: z.string(),
        note: z.string().optional(),
        lines: z.array(z.strictObject({ lineNo: z.number().int(), quantity: DecimalStringSchema })),
      }),
    )
    .optional(),
  approvalRequestId: z.string().optional(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type StockDocument = z.infer<typeof StockDocumentSchema>;

export const RecordStockDocumentSchema = z
  .strictObject({
    kind: z.enum(['opening', 'receipt', 'issue', 'return', 'transfer', 'adjustment']),
    warehouseId: RecordIdSchema,
    toWarehouseId: RecordIdSchema.optional(),
    movedOn: BusinessDateSchema,
    lines: z.array(StockLineInputSchema).min(1).max(100),
    issuedTo: z
      .strictObject({
        kind: z.enum(['project', 'person', 'other']),
        projectId: RecordIdSchema.optional(),
        phase: PhaseSchema.optional(),
        costCode: CostCodeSchema.optional(),
        accountId: z.string().min(1).max(200).optional(),
        description: z.string().trim().min(2).max(300).optional(),
      })
      .optional(),
    reason: z.string().trim().min(3).max(500).optional(),
    idempotencyKey: z.string().trim().min(8).max(200),
  })
  .refine(
    (value) => !value.issuedTo || value.issuedTo.kind !== 'project' || !!value.issuedTo.projectId,
    { message: 'PROJECT_REQUIRED', path: ['issuedTo', 'projectId'] },
  )
  .refine((value) => !value.issuedTo || ['issue', 'return'].includes(value.kind), {
    message: 'ISSUED_TO_ONLY_ON_ISSUE_OR_RETURN',
    path: ['issuedTo'],
  })
  .refine((value) => value.kind !== 'transfer' || !!value.toWarehouseId, {
    message: 'TRANSFER_TARGET_REQUIRED',
    path: ['toWarehouseId'],
  })
  .refine((value) => value.kind !== 'transfer' || value.toWarehouseId !== value.warehouseId, {
    message: 'TRANSFER_SAME_WAREHOUSE',
    path: ['toWarehouseId'],
  })
  .refine((value) => value.kind !== 'issue' || !!value.issuedTo, {
    message: 'ISSUE_TARGET_REQUIRED',
    path: ['issuedTo'],
  })
  .refine(
    (value) =>
      value.kind !== 'adjustment' ||
      (value.lines.every((line) => !!line.direction) && !!value.reason),
    { message: 'ADJUSTMENT_DIRECTION_AND_REASON_REQUIRED', path: ['lines'] },
  );
export type RecordStockDocument = z.infer<typeof RecordStockDocumentSchema>;

export const StockDocumentActionSchema = z.strictObject({
  reason: z.string().trim().min(3).max(500).optional(),
  expectedVersion: z.number().int().positive(),
});

/**
 * The receiving keeper acknowledges what arrived (2026-10-17). Without `lines` everything still in
 * transit is accepted. With `lines`, only the quantities given (in each line's own unit) enter the
 * store; the rest stays in transit — for a later acknowledgement, or to go back to the source store
 * when the transfer is cancelled — and a `reason` (damaged, short) is required.
 */
export const AcknowledgeTransferSchema = z.strictObject({
  lines: z
    .array(z.strictObject({ lineNo: z.number().int().positive(), quantity: DecimalStringSchema }))
    .min(1)
    .max(200)
    .optional(),
  reason: z.string().trim().min(3).max(500).optional(),
  expectedVersion: z.number().int().positive(),
});
export type AcknowledgeTransfer = z.infer<typeof AcknowledgeTransferSchema>;

export const StockDocumentQuerySchema = z.strictObject({
  warehouseId: RecordIdSchema.optional(),
  kind: StockDocumentKindSchema.optional(),
  state: StockDocumentStateSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.string().min(1).max(200).optional(),
});
export type StockDocumentQuery = z.infer<typeof StockDocumentQuerySchema>;

/* -------------------------------------------------------- balances, ledger */

export const StockBalanceSchema = z.strictObject({
  warehouseId: RecordIdSchema,
  itemId: RecordIdSchema,
  itemCode: z.string(),
  itemName: z.string(),
  batch: z.string().optional(),
  quantity: DecimalStringSchema,
  baseUnit: z.string(),
  reorderLevel: DecimalStringSchema.optional(),
  belowReorder: z.boolean(),
});
export type StockBalance = z.infer<typeof StockBalanceSchema>;

export const STOCK_MOVEMENT_KINDS = [
  'opening',
  'receipt',
  'issue',
  'return',
  'transferOut',
  'transferIn',
  'adjustmentIn',
  'adjustmentOut',
  'reversal',
  'supplierReturn',
] as const;
export const StockMovementKindSchema = z.enum(STOCK_MOVEMENT_KINDS);

export const StockMovementSchema = z.strictObject({
  movementId: RecordIdSchema,
  documentId: RecordIdSchema,
  documentNumber: z.string(),
  kind: StockMovementKindSchema,
  warehouseId: RecordIdSchema,
  itemId: RecordIdSchema,
  batch: z.string().optional(),
  /** Signed, base units. */
  quantity: DecimalStringSchema,
  /** The store's balance after this movement, for the running column of the ledger. */
  balanceAfter: DecimalStringSchema,
  movedOn: BusinessDateSchema,
  recordedByAccountId: z.string(),
  createdAt: InstantSchema,
});
export type StockMovement = z.infer<typeof StockMovementSchema>;

export const StockBalanceQuerySchema = z.strictObject({
  warehouseId: RecordIdSchema.optional(),
  /** With a warehouse: include every store under it (the hierarchy, WH-STORE-002). */
  includeChildren: z.enum(['true', 'false']).optional(),
  itemId: RecordIdSchema.optional(),
  belowReorder: z.enum(['true', 'false']).optional(),
});

export const StockLedgerQuerySchema = z.strictObject({
  warehouseId: RecordIdSchema.optional(),
  itemId: RecordIdSchema,
  from: BusinessDateSchema.optional(),
  to: BusinessDateSchema.optional(),
  limit: z.coerce.number().int().min(1).max(500).default(200),
});

/* -------------------------------------------------------------- counts */

export const STOCK_COUNT_STATES = ['open', 'submitted', 'closed', 'cancelled'] as const;
export const StockCountStateSchema = z.enum(STOCK_COUNT_STATES);

/**
 * `full`: every item in the store. `partial`: chosen items. `cycle`: the items of one configured
 * category (a rolling programme). Only the counted items are frozen while the count is open.
 */
export const STOCK_COUNT_SCOPES = ['full', 'partial', 'cycle'] as const;
export const StockCountScopeSchema = z.enum(STOCK_COUNT_SCOPES);

export const StockCountSchema = z.strictObject({
  countId: RecordIdSchema,
  number: z.string(),
  warehouseId: RecordIdSchema,
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema,
  state: StockCountStateSchema,
  countedOn: BusinessDateSchema,
  scope: StockCountScopeSchema,
  /** Partial counts: the items counted. Cycle counts: the category. */
  itemIds: z.array(RecordIdSchema).optional(),
  categoryCode: z.string().optional(),
  /** Blind: counters do not see the book quantity; only a reviewer does (`expected` withheld). */
  blind: z.boolean(),
  /** The cutoff: book quantities were captured at this instant; later movements wait. */
  cutoffAt: InstantSchema,
  lines: z.array(
    z.strictObject({
      itemId: RecordIdSchema,
      itemCode: z.string(),
      itemName: z.string(),
      batch: z.string().optional(),
      baseUnit: z.string(),
      /** The store's quantity at the cutoff; withheld from counters of a blind count. */
      expected: DecimalStringSchema.optional(),
      counted: DecimalStringSchema.optional(),
      difference: DecimalStringSchema.optional(),
      /** Earlier figures replaced by a recount, oldest first. */
      recounts: z.array(
        z.strictObject({
          counted: DecimalStringSchema,
          byAccountId: z.string(),
          at: InstantSchema,
          reason: z.string(),
        }),
      ),
    }),
  ),
  /** Who entered figures: none of them may approve the differences (independent approval). */
  countedByAccountIds: z.array(z.string()),
  /** The adjustment the differences produced, once closed. */
  adjustmentDocumentId: RecordIdSchema.optional(),
  closedByAccountId: z.string().optional(),
  /** Whether the reader sees book quantities (false for a counter on a blind count). */
  expectedVisible: z.boolean(),
  openedByAccountId: z.string(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type StockCount = z.infer<typeof StockCountSchema>;

export const OpenStockCountSchema = z
  .strictObject({
    warehouseId: RecordIdSchema,
    countedOn: BusinessDateSchema,
    scope: StockCountScopeSchema.default('full'),
    itemIds: z.array(RecordIdSchema).min(1).max(500).optional(),
    categoryCode: ReferenceCodeSchema.optional(),
    blind: z.boolean().default(false),
    idempotencyKey: z.string().trim().min(8).max(200),
  })
  .refine((value) => value.scope !== 'partial' || !!value.itemIds?.length, {
    message: 'COUNT_ITEMS_REQUIRED',
    path: ['itemIds'],
  })
  .refine((value) => value.scope !== 'cycle' || !!value.categoryCode, {
    message: 'COUNT_CATEGORY_REQUIRED',
    path: ['categoryCode'],
  });
export type OpenStockCount = z.input<typeof OpenStockCountSchema>;

/** Count again: the earlier figures are kept, the lines wait for new ones. */
export const RecountSchema = z.strictObject({
  lines: z
    .array(z.strictObject({ itemId: RecordIdSchema, batch: z.string().optional() }))
    .min(1)
    .max(500),
  reason: z.string().trim().min(3).max(500),
  expectedVersion: z.number().int().positive(),
});

/** The count report: book and counted figures, differences and their value where stock is valued. */
export const StockCountReportSchema = z.strictObject({
  count: z.lazy(() => StockCountSchema),
  warehouse: z.strictObject({ code: z.string(), name: z.string() }),
  lines: z.array(
    z.strictObject({
      itemId: RecordIdSchema,
      itemCode: z.string(),
      itemName: z.string(),
      batch: z.string().optional(),
      baseUnit: z.string(),
      expected: DecimalStringSchema,
      counted: DecimalStringSchema.optional(),
      difference: DecimalStringSchema.optional(),
      /** Difference × average cost, where valuation is adopted and the item has an average. */
      differenceValue: DecimalStringSchema.optional(),
      recounts: z.number().int().nonnegative(),
    }),
  ),
  totals: z.strictObject({
    lines: z.number().int().nonnegative(),
    counted: z.number().int().nonnegative(),
    withDifference: z.number().int().nonnegative(),
    gainValue: DecimalStringSchema.optional(),
    lossValue: DecimalStringSchema.optional(),
  }),
  currency: z.string().optional(),
  generatedAt: InstantSchema,
});
export type StockCountReport = z.infer<typeof StockCountReportSchema>;

export const EnterCountsSchema = z.strictObject({
  counts: z
    .array(
      z.strictObject({
        itemId: RecordIdSchema,
        batch: z.string().optional(),
        counted: DecimalStringSchema.refine((value) => !value.startsWith('-'), {
          message: 'QUANTITY_EXPECTED',
        }),
      }),
    )
    .min(1)
    .max(1000),
  expectedVersion: z.number().int().positive(),
});

/* ------------------------------------------------------ policy, approvals */

/** `BD-48`: null = not decided; treated as refuse (the conservative default the register proposes). */
export const NegativeStockPolicySchema = z.enum(['refuseNegative', 'allowNegative']).nullable();

export const STORES_APPROVAL_OPERATIONS = {
  /** A stock adjustment or count difference: applied at once unless a policy governs it. */
  adjustment: 'stores.adjustment',
} as const;

/* ------------------------------------------------------- stock valuation (BD-67) */

/**
 * Only weighted average is implemented: one running average per legal entity and item across all of
 * its stores (a transfer moves no value). Adopted explicitly in Settings; from then on openings and
 * receipts carry a cost, and issues, returns and adjustments are valued at the average. Quantities
 * held before adoption are **unvalued** until an audited opening valuation gives them a cost.
 */
export const STOCK_VALUATION_METHODS = ['weightedAverage'] as const;
export const StockValuationMethodSchema = z.enum(STOCK_VALUATION_METHODS).nullable();

export const StockValuationSchema = z.strictObject({
  legalEntityId: RecordIdSchema,
  itemId: RecordIdSchema,
  currency: z.string(),
  onHand: DecimalStringSchema,
  valuedQuantity: DecimalStringSchema,
  unvaluedQuantity: DecimalStringSchema,
  value: DecimalStringSchema,
  /** Value ÷ valued quantity, to 4 places; absent while nothing is valued. */
  averageCost: DecimalStringSchema.optional(),
});
export type StockValuation = z.infer<typeof StockValuationSchema>;

export const OpeningValuationSchema = z.strictObject({
  legalEntityId: RecordIdSchema,
  itemId: RecordIdSchema,
  /** Cost per base unit of the quantity on hand that has no value yet. */
  unitCost: DecimalStringSchema,
  reason: z.string().trim().min(3).max(500),
});
export type OpeningValuation = z.infer<typeof OpeningValuationSchema>;

/* ----------------------------------------------------------------- custody */

/**
 * Custody (أمانات). `outgoing`: the company's material handed to someone who must return it — it
 * leaves the store through an issue and comes back through a return; what is still out is the
 * holder's responsibility. `incoming`: someone else's material the company holds (a client's or a
 * contractor's) — never the company's stock, never valued, kept apart from every balance, and handed
 * back to its owner (WH-CUSTODY-001).
 */
export const CUSTODY_DIRECTIONS = ['outgoing', 'incoming'] as const;
export const CustodyDirectionSchema = z.enum(CUSTODY_DIRECTIONS);
export const CUSTODY_COUNTERPARTIES = [
  'employee',
  'contractor',
  'supplier',
  'client',
  'other',
] as const;
export const CUSTODY_STATES = ['open', 'closed'] as const;
export const CUSTODY_EVENTS = ['opened', 'returned', 'lost'] as const;

export const CounterpartySchema = z.strictObject({
  kind: z.enum(CUSTODY_COUNTERPARTIES),
  /** For an employee: their account; otherwise the name as written. */
  accountId: z.string().min(1).max(200).optional(),
  name: z.string().trim().min(2).max(160),
});

export const StockCustodySchema = z.strictObject({
  custodyId: RecordIdSchema,
  number: z.string(),
  direction: CustodyDirectionSchema,
  state: z.enum(CUSTODY_STATES),
  warehouseId: RecordIdSchema,
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema,
  projectId: RecordIdSchema.optional(),
  counterparty: CounterpartySchema,
  openedOn: BusinessDateSchema,
  expectedReturnOn: BusinessDateSchema.optional(),
  lines: z.array(
    z.strictObject({
      lineNo: z.number().int().positive(),
      itemId: RecordIdSchema,
      itemCode: z.string(),
      itemName: z.string(),
      batch: z.string().optional(),
      baseUnit: z.string(),
      quantity: DecimalStringSchema,
      returned: DecimalStringSchema,
      lost: DecimalStringSchema,
      outstanding: DecimalStringSchema,
    }),
  ),
  events: z.array(
    z.strictObject({
      kind: z.enum(CUSTODY_EVENTS),
      on: BusinessDateSchema,
      lines: z.array(
        z.strictObject({ lineNo: z.number().int().positive(), quantity: DecimalStringSchema }),
      ),
      reason: z.string().optional(),
      /** The stock document it moved (outgoing custody only). */
      documentId: RecordIdSchema.optional(),
      byAccountId: z.string(),
      at: InstantSchema,
    }),
  ),
  overdue: z.boolean(),
  reason: z.string(),
  recordedByAccountId: z.string(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type StockCustody = z.infer<typeof StockCustodySchema>;

export const OpenCustodySchema = z.strictObject({
  direction: CustodyDirectionSchema,
  warehouseId: RecordIdSchema,
  counterparty: CounterpartySchema,
  openedOn: BusinessDateSchema,
  expectedReturnOn: BusinessDateSchema.optional(),
  projectId: RecordIdSchema.optional(),
  lines: z
    .array(
      z.strictObject({
        itemId: RecordIdSchema,
        quantity: QuantitySchema,
        unit: UnitSchema,
        batch: z.string().trim().min(1).max(60).optional(),
      }),
    )
    .min(1)
    .max(100),
  reason: z.string().trim().min(3).max(500),
  idempotencyKey: z.string().trim().min(8).max(200),
});
export type OpenCustody = z.infer<typeof OpenCustodySchema>;

/** Settle part of a custody: returned (back to the store, or to its owner) or lost (with a reason). */
export const SettleCustodySchema = z.strictObject({
  outcome: z.enum(['returned', 'lost']),
  on: BusinessDateSchema,
  lines: z
    .array(z.strictObject({ lineNo: z.number().int().positive(), quantity: QuantitySchema }))
    .min(1)
    .max(100),
  reason: z.string().trim().min(3).max(500).optional(),
  idempotencyKey: z.string().trim().min(8).max(200),
  expectedVersion: z.number().int().positive(),
});
export type SettleCustody = z.infer<typeof SettleCustodySchema>;

export const CustodyQuerySchema = z.strictObject({
  direction: CustodyDirectionSchema.optional(),
  state: z.enum(CUSTODY_STATES).optional(),
  warehouseId: RecordIdSchema.optional(),
  itemId: RecordIdSchema.optional(),
});

/* --------------------------------------------------------------- reports */

/** One item across the company: where it is, what is moving, what is held by or for others. */
export const ItemCardSchema = z.strictObject({
  item: z.lazy(() => StockItemSchema),
  stores: z.array(
    z.strictObject({
      warehouseId: RecordIdSchema,
      code: z.string(),
      name: z.string(),
      kind: WarehouseKindSchema,
      batch: z.string().optional(),
      quantity: DecimalStringSchema,
    }),
  ),
  totals: z.strictObject({
    /** In the company's stores (base units). */
    onHand: DecimalStringSchema,
    /** Left one store and not yet acknowledged by the other. */
    inTransit: DecimalStringSchema,
    /** The company's, held by someone who must return it. */
    outgoingCustody: DecimalStringSchema,
    /** Someone else's, held by the company — never part of the company's stock. */
    incomingCustody: DecimalStringSchema,
  }),
  valuation: z
    .strictObject({
      currency: z.string(),
      value: DecimalStringSchema,
      averageCost: DecimalStringSchema.optional(),
      unvaluedQuantity: DecimalStringSchema,
    })
    .optional(),
  movements: z.array(z.lazy(() => StockMovementSchema)),
  custody: z.array(
    z.strictObject({
      custodyId: RecordIdSchema,
      number: z.string(),
      direction: CustodyDirectionSchema,
      counterparty: z.string(),
      outstanding: DecimalStringSchema,
      overdue: z.boolean(),
    }),
  ),
  generatedAt: InstantSchema,
});
export type ItemCard = z.infer<typeof ItemCardSchema>;

export const MovementReportQuerySchema = z.strictObject({
  warehouseId: RecordIdSchema.optional(),
  includeChildren: z.enum(['true', 'false']).optional(),
  itemId: RecordIdSchema.optional(),
  from: BusinessDateSchema,
  to: BusinessDateSchema,
});

/** Per store and item: opening, in, out and closing for a period, from the stock ledger. */
export const MovementReportSchema = z.strictObject({
  from: BusinessDateSchema,
  to: BusinessDateSchema,
  rows: z.array(
    z.strictObject({
      warehouseId: RecordIdSchema,
      warehouseCode: z.string(),
      itemId: RecordIdSchema,
      itemCode: z.string(),
      itemName: z.string(),
      baseUnit: z.string(),
      opening: DecimalStringSchema,
      in: DecimalStringSchema,
      out: DecimalStringSchema,
      closing: DecimalStringSchema,
    }),
  ),
  generatedAt: InstantSchema,
});
export type MovementReport = z.infer<typeof MovementReportSchema>;

/**
 * Project material accountability (WH-PROJECT-001): for each item, what reached the project's stores,
 * what is still on the way, what was consumed (by phase), returned, sent away, adjusted and is on hand —
 * and whether those figures reconcile with the stores' balances. Quantities in base units.
 */
export const ProjectMaterialsSchema = z.strictObject({
  projectId: RecordIdSchema,
  stores: z.array(
    z.strictObject({ warehouseId: RecordIdSchema, code: z.string(), name: z.string() }),
  ),
  items: z.array(
    z.strictObject({
      itemId: RecordIdSchema,
      itemCode: z.string(),
      itemName: z.string(),
      baseUnit: z.string(),
      /** Transfers toward the project's stores: acknowledged plus still in transit. */
      dispatched: DecimalStringSchema,
      inTransit: DecimalStringSchema,
      /** Into the project's stores: acknowledged transfers, direct receipts and openings. */
      received: DecimalStringSchema,
      /** Issued to the project (from any store), less returns from use. */
      consumed: DecimalStringSchema,
      /** Issued from the project's stores to anything other than the project (people, custody). */
      issuedElsewhere: DecimalStringSchema,
      /** Transfers out of the project's stores (back to central or to another project). */
      transferredOut: DecimalStringSchema,
      /** Approved adjustments and count differences in the project's stores (signed). */
      adjusted: DecimalStringSchema,
      onHand: DecimalStringSchema,
      /** received − issued from the stores + returns − transferred out + adjusted, against on hand. */
      reconciles: z.boolean(),
    }),
  ),
  byPhase: z.array(
    z.strictObject({
      phase: z.string().nullable(),
      costCode: z.string().nullable(),
      itemId: RecordIdSchema,
      consumed: DecimalStringSchema,
      value: MoneySchema.optional(),
    }),
  ),
  generatedAt: InstantSchema,
});
export type ProjectMaterials = z.infer<typeof ProjectMaterialsSchema>;

export const STORES_AUDIT_ACTIONS = {
  itemCreated: 'stores.item.created',
  itemUpdated: 'stores.item.updated',
  warehouseCreated: 'stores.warehouse.created',
  warehouseUpdated: 'stores.warehouse.updated',
  documentRecorded: 'stores.document.recorded',
  transferAcknowledged: 'stores.transfer.acknowledged',
  documentCancelled: 'stores.document.cancelled',
  documentDecided: 'stores.document.decided',
  documentReversed: 'stores.document.reversed',
  countOpened: 'stores.count.opened',
  countEntered: 'stores.count.entered',
  countClosed: 'stores.count.closed',
  countRecounted: 'stores.count.recounted',
  custodyOpened: 'stores.custody.opened',
  custodySettled: 'stores.custody.settled',
  supplierReturned: 'stores.supplierReturn.recorded',
  openingValued: 'stores.valuation.opening',
} as const;
